import { catchError, map } from 'rxjs/operators';
import { forkJoin, of } from 'rxjs';
import { Injectable } from '@angular/core';
import { ApiService } from './api';
import { PartnerType } from '../admin/admin-partners/partner-type.enum';

export interface PartnershipPayload {
  id: number;
  enabled?: boolean;
  access_type_id?: number;
  codeshares_enabled?: boolean;
  upa_subscriptions_attributes?: any[];
  upa_category_subscriptions_attributes?: any[];
}

export enum BulkAction {
    ACTIVATE = 'activate',
    DEACTIVATE = 'deactivate',
}

@Injectable({ providedIn: 'root' })
export class PartnerAccessService {
  path = '/api';
  constructor(private apiService: ApiService) { }

  // airlines to distributors
  getPartnerships(partnerId: any) {
    return this.apiService.get(`${this.path}/partnerships?airline_id=${partnerId}`);
  }

  // allows setting of the idType by partnerType
  getPartnershipsByType(partnerId: any, partnerType: PartnerType) {
    const idType = partnerType === PartnerType.AIRLINE ? 'airline_id' : 'partner_id';
    return this.apiService.get(`${this.path}/partnerships?${idType}=${partnerId}`);
  }

  getPartners() {
    const observableAirlines = this.apiService.get(`${this.path}/v2/airlines`).pipe(catchError(() => of([])));
    const observableDistrbutors = this.apiService.get(`${this.path}/distributors`).pipe(catchError(() => of([])));
    const observablePlatforms = this.apiService.get(`${this.path}/platforms`).pipe(catchError(() => of([])));
    return forkJoin([observableAirlines, observableDistrbutors, observablePlatforms]).pipe(
      map((response: any) => {
        return response[0].concat(response[1], response[2]);
      }),
    );
  }

  getPartnershipById(partnershipId: any) {
    return this.apiService.get(`${this.path}/v2/partnerships/${partnershipId}`);
  }

  /**
   * Replace partnership categories access
   * 
   * Previously-assigned categories will be replaced 
   * with a new set of categories in `payload`.
   */
  replacePartnershipsAccess(payload: PartnershipPayload[]) {
    return this.apiService
      .put<{ id: number }>(`${this.path}/v2/partnerships`, {
        bulk_partnership: {
          data: payload,
        },
      });
  }

  /**
   * Change partnerships categories access
   * 
   * The difference between this method and `replacePartnershipsAccess` is that
   * this method will act as `append` while `replacePartnershipsAccess` erises 
   * previous data and replace it with new set of access categories.
   */
  changePartnershipsAccess(payload: PartnershipPayload[], action: BulkAction) {
    return this.apiService
      .put(`${this.path}/v2/partnerships/${action}`, {
        bulk_partnership: {
          data: payload,
        },
      });
  }

  getPartnershipsProcessingStatus(id: number) {
    return this.apiService.get<{ status: string }>(`${this.path}/v2/partnerships/update_status/${id}`);
  }
}
